import { useState } from "react";
import { useTranslations } from "use-intl";
import { map } from "lodash-es";
import { Button } from "@mui/material";
import { SwipeableDrawer } from "../SwipeableDrawer";
import { DrawerTopBarWithAction } from "../Drawer";
import { AirlinesFilter } from "./flight-filters/AirlinesFilter";
import type { Airline } from "@/app/lib/types/interfaces";
import { useToggleSelectAll } from "@/app/lib/hooks/useToggleSelectAll";

type AirlinesFilterContentsProps = {
  allAirlines?: Airline[];
  defaultFilter?: string[];
  value?: string[];
  onSubmit: (newValue: string[]) => void;
  onCloseClick: () => void;
};

export const AirlinesFilterContents = ({
  allAirlines = [],
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: AirlinesFilterContentsProps) => {
  const t = useTranslations();

  const [localFilter, setLocalFilter] = useState(value);
  const allAirlinesCodes = map(allAirlines, "code");

  const {
    manuallySelectedAll,
    setManuallySelectedAll,
    areAllSelected,
    toggle,
  } = useToggleSelectAll<string>({
    selected: localFilter,
    all: allAirlinesCodes,
  });

  const isInDefaultState = areAllSelected && !manuallySelectedAll;

  return (
    <>
      <DrawerTopBarWithAction
        onCloseClick={onCloseClick}
        title={t("flights.airlines")}
        actionElement={
          allAirlines.length > 1 ? (
            <Button
              onClick={(e) => {
                if (isInDefaultState) {
                  setLocalFilter(allAirlinesCodes);
                  setManuallySelectedAll(true);
                } else {
                  setLocalFilter(defaultFilter);
                  setManuallySelectedAll(false);
                }

                // Remove focus from the button after click
                e.currentTarget.blur();
              }}
              variant="text"
              size="small"
            >
              {isInDefaultState ? t("selectAll") : t("clearFilter")}
            </Button>
          ) : null
        }
      />

      <div className="-mx-4 my-2 flex-grow overflow-y-auto px-4">
        <AirlinesFilter
          airlines={allAirlines}
          selectedAirlines={localFilter}
          userManuallySelectedAllAirlines={manuallySelectedAll}
          onSingleAirlineChange={(e, checked, airline) => {
            e.stopPropagation();
            setLocalFilter((prev) => toggle(prev, airline.code, checked));
          }}
        />
      </div>

      <div className="-mx-4 border-t border-t-stroke-neutral-soft px-4 pt-4">
        <Button
          fullWidth
          onClick={() => onSubmit(localFilter)}
          variant="contained"
          size="large"
        >
          {t("apply")}
        </Button>
      </div>
    </>
  );
};

type Props = AirlinesFilterContentsProps & {
  open: boolean;
};

export const AirlinesFilterDrawer = ({
  open,
  allAirlines = [],
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: Props) => {
  return (
    <SwipeableDrawer open={open} onClose={onCloseClick}>
      {/* need to reset the state when drawer is open/closed */}
      {open ? (
        <AirlinesFilterContents
          allAirlines={allAirlines}
          defaultFilter={defaultFilter}
          value={value}
          onSubmit={onSubmit}
          onCloseClick={onCloseClick}
        />
      ) : null}
    </SwipeableDrawer>
  );
};
